import React, { useEffect, useRef, useState } from 'react';
import { signInWithRedirect, getCurrentUser, fetchAuthSession, signOut, deleteUser } from 'aws-amplify/auth';

// Mock products — all three now purchasable
const mockProducts = [
  { 
    id: '1', 
    name: 'AI Email Funnel', 
    description: 'Automate lead responses with AI.', 
    price: 29, 
    priceId: 'price_1T85YAKoipbhfW3DHInOf6f6', 
    previewUrl: 'https://picsum.photos/300/200?random=1' 
  },
  { 
    id: '2', 
    name: 'Content Calendar Generator', 
    description: 'AI generates 30-day plan.', 
    price: 19, 
    priceId: 'price_1T48qXKoipbhfW3D9yhLH915', 
    previewUrl: 'https://picsum.photos/300/200?random=2' 
  },
  { 
    id: '3', 
    name: 'Lead Scoring Bot', 
    description: 'Score leads via sentiment analysis.', 
    price: 39, 
    priceId: 'price_1T48zrKoipbhfW3DxTTTcb2N', 
    previewUrl: 'https://picsum.photos/300/200?random=3' 
  },
];

interface Product { id: string; name: string; description: string; price: number; previewUrl: string; priceId: string; }

const MarketplaceApp = () => {
  const location = window.location.pathname;
  const hasSentEmail = useRef(false);
  const [user, setUser] = useState<{ email: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Read the correct product name from the URL
  const urlParams = new URLSearchParams(window.location.search);
  const productName = decodeURIComponent(urlParams.get('product') || 'AI Email Funnel');

  const priceMap: Record<string, number> = {
    'AI Email Funnel': 29,
    'Content Calendar Generator': 19,
    'Lead Scoring Bot': 39
  };
  const amount = priceMap[productName] ?? 29;

  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      try {
        const currentUser = await getCurrentUser();
        const session = await fetchAuthSession();

        const idTokenPayload = session.tokens?.idToken?.payload as { email?: string } | undefined;
        const email = idTokenPayload?.email ||
                      currentUser.signInDetails?.loginId ||
                      currentUser.username ||
                      'unknown';

        setUser({ email });
      } catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Send email on success page
  useEffect(() => {
    if (location !== '/success' || hasSentEmail.current) return;

    hasSentEmail.current = true;

    const sendEmail = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const product = urlParams.get('product') || 'AI Email Funnel';

        const payload = {
          email: user?.email || 'watkinson@rocketmail.com',
          product: product
        };

        const response = await fetch('https://ji0c1x4mn0.execute-api.us-east-1.amazonaws.com/prod/send-download-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      } catch (err) {
        console.error('Email send failed:', err);
      }
    };

    sendEmail();
  }, [location, user]);

  const handlePurchase = async (product: Product) => {
    try {
      const response = await fetch('https://o73xzojmb4.execute-api.us-east-1.amazonaws.com/prod/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          priceId: product.priceId, 
          quantity: 1,
          productName: product.name 
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      alert('Purchase failed: ' + (error as Error).message);
    }
  };

  const handleDeleteAccount = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await deleteUser();
      alert('Your account has been deleted. You are now signed out.');
      window.location.replace('/');
    } catch (error) {
      console.error('Delete account failed:', error);
      alert('Failed to delete account. Please try again or contact support.');
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  if (location === '/success') {
    return (
      <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
        <header style={{ textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '60px 20px', borderRadius: '12px 12px 0 0' }}>
          <h1 style={{ margin: '0 0 16px', fontSize: '3rem' }}>Payment Successful!</h1>
          <p>Your workflow is on its way. Check your email for the download link.</p>
        </header>

        <section style={{ background: '#fff', borderRadius: '0 0 12px 12px', padding: '40px 20px' }}>
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#27ae60', fontSize: '2rem' }}>Order Summary</h2>
            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', maxWidth: '500px', margin: '0 auto' }}>
              <p style={{ fontSize: '1.2rem', margin: '8px 0' }}>
                <strong>Item:</strong> {productName}
              </p>
              <p style={{ fontSize: '1.2rem', margin: '8px 0' }}>
                <strong>Amount:</strong> ${amount}.00
              </p>
            </div>
          </div>

          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#27ae60', fontSize: '1.8rem', marginBottom: '16px' }}>Next Steps</h2>
            <ul style={{ 
              textAlign: 'left', 
              maxWidth: '600px', 
              margin: '0 auto 24px', 
              paddingLeft: '20px', 
              fontSize: '1.1rem', 
              lineHeight: 1.6 
            }}>
              <li>Check your email (including spam/promotions) for the download ZIP.</li>
              <li>The ZIP includes prompts, setup guide, and everything you need to get started.</li>
              <li>Integrate with Zapier for automatic delivery on future purchases.</li>
              <li>Questions? Reply directly to the email or contact support@yourapp.com.</li>
            </ul>
          </div>
          
          <button 
            onClick={() => window.location.replace('/')}
            style={{ background: '#27ae60', color: 'white', padding: '16px 40px', border: 'none', borderRadius: '8px', fontSize: '1.2rem', cursor: 'pointer' }}
          >
            Back to Marketplace
          </button>
        </section>
      </div>
    );
  }

  if (location === '/support') {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
        <h1>Support & Policies</h1>
  
        <section style={{ marginBottom: '40px' }}>
          <h2>Customer Service Contact</h2>
          <p>Email: support@aiworkflowmarket.com</p>
          <p>We respond within 24–48 hours. For urgent issues, include your order ID or purchase email.</p>
        </section>
  
        <section style={{ marginBottom: '40px' }}>
          <h2>Refund Policy</h2>
          <p>All products are digital downloads (ZIP files with AI prompts/templates). Due to instant delivery, refunds are limited to cases of technical issues or non-delivery. Contact support within 7 days of purchase for review. No refunds for buyer's remorse or change of mind.</p>
        </section>
  
        <section style={{ marginBottom: '40px' }}>
          <h2>Dispute/Chargeback Policy</h2>
          <p>We handle disputes directly — contact support first before initiating chargeback. We will review and resolve valid issues promptly. Chargebacks may result in account suspension.</p>
        </section>
  
        <section style={{ marginBottom: '40px' }}>
          <h2>Cancellation Policy</h2>
          <p>No recurring subscriptions — all purchases are one-time. No cancellations needed.</p>
        </section>
  
        <section style={{ marginBottom: '40px' }}>
          <h2>Terms & Promotions</h2>
          <p>Any promotions (e.g., early access perks) are limited-time and subject to change. Full terms apply at time of purchase. No legal/export restrictions — products are digital and compliant for global use.</p>
        </section>
  
        <p style={{ marginTop: '40px', color: '#888' }}>
          Last updated: March 11, 2026
        </p>
  
        <button 
          onClick={() => window.location.href = '/'}
          style={{ background: '#667eea', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', marginTop: '40px', fontSize: '1.1rem' }}
        >
          Back to Marketplace
        </button>
      </div>
    );
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Checking authentication...</div>;

  if (!user) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '20px' }}>AI Workflow Marketplace</h1>
        <p style={{ fontSize: '1.3rem', marginBottom: '40px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
          Browse and purchase ready-to-use AI workflows for marketing, productivity, and automation. Log in or sign up to buy.
        </p>
  
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px' }}>
          {mockProducts.map((p) => (
            <div key={p.id} style={{ 
              border: '1px solid #ddd', 
              padding: '20px', 
              borderRadius: '12px', 
              textAlign: 'center',
              background: '#fff',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <img src={p.previewUrl} alt={p.name} style={{ maxWidth: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }} />
              <h3 style={{ margin: '16px 0 8px' }}>{p.name}</h3>
              <p style={{ color: '#555', marginBottom: '16px' }}>{p.description}</p>
              <p style={{ fontSize: '1.8rem', color: '#e74c3c', fontWeight: 'bold' }}>${p.price}</p>
              <p style={{ color: '#888', marginBottom: '16px' }}>Login to purchase</p>
            </div>
          ))}
        </div>
  
        <div style={{ marginTop: '60px' }}>
          <button onClick={() => signInWithRedirect()} style={{ background: '#667eea', color: 'white', padding: '16px 40px', borderRadius: '8px', border: 'none', marginRight: '20px', fontSize: '1.2rem' }}>
            Login
          </button>
          <button onClick={() => signInWithRedirect()} style={{ background: '#764ba2', color: 'white', padding: '16px 40px', borderRadius: '8px', border: 'none', fontSize: '1.2rem' }}>
            Sign Up
          </button>
        </div>
        <div style={{ marginTop: '40px' }}>
          <a href="/support" style={{ color: '#667eea', textDecoration: 'underline', fontSize: '1.1rem' }}>
            Support & Policies
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto', position: 'relative' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '20px', borderRadius: '12px' }}>
        <h1>AI Workflow Marketplace</h1>
        <div>
          Welcome, {user.email} |{' '}
          <button 
            onClick={async () => {
              try {
                await signOut({ global: true });
              } catch (err) {
                console.error('signOut error:', err);
              }

              localStorage.clear();
              sessionStorage.clear();

              if (window.indexedDB) {
                const databases = await window.indexedDB.databases?.();
                if (databases) {
                  databases.forEach(db => {
                    if (db.name) window.indexedDB.deleteDatabase(db.name);
                  });
                }
              }

              document.cookie.split(";").forEach(c => {
                document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
              });

              window.location.replace('/');
            }}
            style={{ color: 'white', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Logout
          </button>
          {' | '}
          <button 
            onClick={handleDeleteAccount} 
            style={{ color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
          >
            Delete Account
          </button>
        </div>
      </header>

      <section>
        <h2>Available Workflows</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '25px' }}>
          {mockProducts.map((p) => (
            <div key={p.id} style={{ border: '1px solid #ddd', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
              <img src={p.previewUrl} alt={p.name} style={{ maxWidth: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }} />
              <h3>{p.name}</h3>
              <p>{p.description}</p>
              <p style={{ fontSize: '1.5em', color: '#e74c3c', fontWeight: 'bold' }}>${p.price}</p>
              <button onClick={() => handlePurchase(p)} style={{ background: '#27ae60', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '6px' }}>
                Buy Now with Stripe
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Custom Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ marginBottom: '16px', color: '#333' }}>Delete Account?</h3>
            <p style={{ marginBottom: '24px', color: '#555' }}>
              This action is permanent and cannot be undone. All your data will be lost.
            </p>
            <div>
              <button
                onClick={confirmDelete}
                style={{
                  backgroundColor: '#ff4d4f',
                  color: 'white',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  marginRight: '12px',
                }}
              >
                Yes, Delete Permanently
              </button>
              <button
                onClick={cancelDelete}
                style={{
                  backgroundColor: '#ccc',
                  color: '#333',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return <MarketplaceApp />;
}

export default App;